import { useState, useEffect, useRef } from "react";
import { ClassMeta } from "./utils/classCatalog";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "./App_settings.css";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

interface LeftPaneProps {
  classMeta?: ClassMeta;
  classes: ClassMeta[];
  mode: "regular" | "fixed" | "suite";
  onBrowseClass?: (direction: "next" | "prev") => void;
  suiteImages?: { src: string; alt: string }[];
  pricing: { guestRange?: string; pricing: string };
}

function computeBrightness(img: HTMLImageElement): Promise<number> {
  return new Promise((resolve, reject) => {
    // Wait until image has valid dimensions
    if (!img.naturalWidth || !img.naturalHeight) {
      img.onload = () => computeBrightness(img).then(resolve).catch(reject);
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Canvas context not available");

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      ctx.drawImage(img, 0, 0);

      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let total = 0;
      const count = data.length / 4;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        total += 0.299 * r + 0.587 * g + 0.114 * b; // perceived brightness
      }

      const avg = total / count;
      resolve(avg);
    } catch (err) {
      reject(err);
    }
  });
}

function updateNavContrast(img: HTMLImageElement, root: HTMLElement | null) {
  console.log("updateNavContrast called", img?.src, root);
  if (!root) return;

  if (!img.complete) {
    img.onload = () => updateNavContrast(img, root);
    return;
  }

  //ensure cross-origin images are handled
  img.crossOrigin = "anonymous";

  computeBrightness(img).then((brightness) => {
    console.log("brightness", brightness);
    if (brightness > 182) {
      root.classList.add("nav-dark");
      root.classList.remove("nav-light");
    } else {
      root.classList.add("nav-light");
      root.classList.remove("nav-dark");
    }
  });
}

export default function LeftPane({ classMeta,  mode,  suiteImages, pricing }: LeftPaneProps) {
  // const guestRange = pricing?.guestRange || "";
  const price = pricing?.pricing || "";

  const [activeSection, setActiveSection] = useState<keyof ClassMeta["sections"] | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const sectionTitles: Record<string, string> = {
    schedule: "Schedule",
    groupSize: "Group Size",
    paymentUrl: "Payment Information",
    faqUrl: "FAQ",
    termsUrl: "Terms and Conditions",
    privacyUrl: "Privacy Policy",
  };

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!suiteImages || suiteImages.length === 0) return;

    let loadedCount = 0;

    suiteImages.forEach(({ src }) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === suiteImages.length) {
          setImagesLoaded(true);
        }
      };
      img.src = src;
    });
  }, [suiteImages]);

  // -------------------------
  // SUITE MODE
  // -------------------------
  if (mode === "suite" && classMeta) {
    const className = classMeta.name;
    const description = classMeta.shortDescription;

    return (
      <div ref={rootRef} className="leftpane-root w-full px-6 py-4 h-full flex flex-col gap-4">
        {/* TOP: Images + Description */}
        <div className="flex flex-col bg-[#f7f0ea] border border-gray-300 rounded-lg p-2 w-full">
          <h2 className="text-lg font-semibold text-center text-gray-800 mb-2">{className}</h2>

          <div className="flex flex-row gap-6 w-full">
            {/* Carousel */}
            <div className="h-[345px] w-[50%] rounded-lg overflow-hidden bg-gray-200">
              {!imagesLoaded ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <>
                  <Swiper
                    modules={[Navigation, Pagination]}
                    navigation
                    pagination={{ clickable: true }}
                    onInit={(swiper) => {
                      const slide = swiper.slides[swiper.activeIndex];
                      const img = slide.querySelector("img") as HTMLImageElement | null;
                      if (img) {
                        console.log("init img", img.src);
                        updateNavContrast(img, rootRef.current);
                      }
                    }}
                    onSlideChange={(swiper) => {
                      const slide = swiper.slides[swiper.activeIndex];
                      const img = slide.querySelector("img") as HTMLImageElement | null;
                      if (img) {
                        console.log("slide img", img.src);
                        updateNavContrast(img, rootRef.current);
                      }
                    }}
                  >
                    {suiteImages.map((img, i) => (
                      <SwiperSlide key={i}>
                        <div
                          className="relative group h-full w-full cursor-pointer zoom-hover"
                          onClick={() => {
                            setLightboxIndex(i);
                            setLightboxOpen(true);
                          }}
                        >
                          <img src={img.src} alt={img.alt || `Suite image ${i + 1}`} className="w-full h-full object-cover" />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <Lightbox
                    open={lightboxOpen}
                    close={() => setLightboxOpen(false)}
                    index={lightboxIndex}
                    slides={suiteImages.map((img) => ({ src: img.src }))}
                  />
                </>
              )}
            </div>

            {/* Description */}
            <div className="flex-1 flex flex-col justify-start">
              <div className="h-[345px] overflow-y-auto pr-1">
                <p className="text-gray-700 text-sm leading-snug whitespace-pre-line">{description}</p>
              </div>
            </div>
          </div>
        </div>

        {/* PRICING */}
        <div className="h-[240px] bg-[#f7f0ea] border border-gray-300 rounded-lg p-3">
          <h3 className="text-sm font-bold uppercase text-gray-700 mb-1">Pricing</h3>

          <div
            className="text-gray-800 text-sm whitespace-pre-line leading-tight"
            dangerouslySetInnerHTML={{
              __html: price.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/__(.*?)__/g, "<u>$1</u>"),
            }}
          />
        </div>

        {/* TABS — SUITE MODE */}
        <div className="grid grid-cols-3 gap-3 text-sm font-medium text-center flex-[1]">
          {[
            ["termsUrl", "Terms and Conditions"],
            ["privacyUrl", "Privacy Policy"],
            ["faqUrl", "FAQ"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveSection(key as keyof ClassMeta["sections"])}
              className="bg-[#f7f0ea] border border-gray-300 rounded py-2 hover:bg-[#e8dcc7] transition"
            >
              {label}
            </button>
          ))}
        </div>

        {/* MODAL */}
        {activeSection && classMeta.sections && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setActiveSection(null)}>
            <div
              className="bg-white w-[95%] max-w-4xl h-[70%] rounded-lg shadow-xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold">{sectionTitles[activeSection] || activeSection}</h3>
                <button className="text-gray-500 hover:text-gray-800" onClick={() => setActiveSection(null)}>
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-auto">
                {classMeta.sections[activeSection].startsWith("http") ? (
                  <iframe src={classMeta.sections[activeSection]} className="w-full h-full border-0" />
                ) : (
                  <div className="p-4 whitespace-pre-line text-sm text-gray-700">{classMeta.sections[activeSection]}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------
  // REGULAR / FIXED MODE
  // -------------------------

  const imageUrl = classMeta?.imageUrl;
  const className =
  (classMeta as any)?.displayName ?? classMeta?.name ?? "Select a Class";
  const description = classMeta?.shortDescription;

  return (
    <div className="w-full px-6 py-4 h-full flex flex-col gap-4">
      {/* TOP */}
      <div className="flex flex-col bg-[#f7f0ea] border border-gray-300 rounded-lg p-2 w-full">
        <h2 className="text-lg font-semibold text-center text-gray-800 mb-2">{className}</h2>

        <div className="flex flex-row gap-6 w-full">
          <div className="h-[345px] w-[50%] rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
            {imageUrl ? (
              <img src={imageUrl} alt={className} className="w-full h-full object-contain shrink-0" />
            ) : (
              <span className="text-gray-500">Image Placeholder</span>
            )}
          </div>

          <div className="flex-1 flex flex-col justify-start">
            <div className="h-[345px] overflow-y-auto pr-1">
              <p className="text-gray-700 text-sm leading-snug whitespace-pre-line">{description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div className="h-[240px] bg-[#f7f0ea] border border-gray-300 rounded-lg p-3">
        <h3 className="text-sm font-bold uppercase text-gray-700 mb-1">Pricing</h3>

        <div
          className="text-gray-800 text-sm whitespace-pre-line leading-tight"
          dangerouslySetInnerHTML={{
            __html: price.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/__(.*?)__/g, "<u>$1</u>"),
          }}
        />
      </div>

      {/* TABS */}
      <div className="grid grid-flow-col auto-cols-max justify-center gap-3 text-sm font-medium text-center flex-[1]">
        {[
          ["schedule", "Schedule"],
          ["groupSize", "Group Size"],
          ["accommodationsUrl", "Accommodations"],
          ["paymentUrl", "Payment"],
          ["faqUrl", "FAQ"],
          
        ].map(([key, label]) => {
          const isAccommodations = key === "accommodationsUrl";

          return (
            <button
              key={key}
              onClick={() => setActiveSection(key as keyof ClassMeta["sections"])}
              className={
                `bg-[#f7f0ea] border border-gray-300 rounded py-2 hover:bg-[#e8dcc7] transition inline-flex items-center justify-center ` +
                (isAccommodations ? "px-4" : "px-2")
              }
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* MODAL */}
      {activeSection && classMeta?.sections && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setActiveSection(null)}>
          <div
            className="bg-white w-[95%] max-w-4xl h-[70%] rounded-lg shadow-xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">{sectionTitles[activeSection] || activeSection}</h3>
              <button className="text-gray-500 hover:text-gray-800" onClick={() => setActiveSection(null)}>
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {classMeta.sections[activeSection].startsWith("http") ? (
                <iframe src={classMeta.sections[activeSection]} className="w-full h-full border-0" />
              ) : (
                <div className="p-4 whitespace-pre-line text-sm text-gray-700">{classMeta.sections[activeSection]}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
